import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { notificationAdminApi } from "../../services/notificationAdminApi";

function MetricCard({ title, value, subtitle }) {
  return (
    <div className="border rounded-2xl p-4 shadow-sm border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
      <div className="text-sm text-gray-500 dark:text-neutral-400">{title}</div>
      <div className="text-2xl font-bold mt-1 text-black dark:text-white">
        {value ?? 0}
      </div>
      {subtitle ? (
        <div className="text-xs mt-2 text-gray-500 dark:text-neutral-400">
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

export default function ITNotificationsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const res = await notificationAdminApi.getSummary();
      setData(res.data);
    } catch (err) {
      console.error("Failed to load notification analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runEscalations = async () => {
    try {
      setActionMsg("");
      const res = await notificationAdminApi.runEscalations();
      setActionMsg(`Escalations: ${JSON.stringify(res.data?.result || {})}`);
      await load();
    } catch (err) {
      console.error(err);
      setActionMsg(
        err?.response?.data?.message || err.message || "Failed to run escalations"
      );
    }
  };

  const funnelData = useMemo(() => {
    if (!data?.funnel) return [];
    return [
      { name: "Created", value: Number(data.funnel.created_total || 0) },
      { name: "Seen", value: Number(data.funnel.seen_total || 0) },
      { name: "Acknowledged", value: Number(data.funnel.acknowledged_total || 0) },
      { name: "Resolved", value: Number(data.funnel.resolved_total || 0) },
    ];
  }, [data]);

  const byTypeData = useMemo(() => {
    return (data?.byType || []).map((x) => ({
      type: x.type_code,
      total: Number(x.total || 0),
      unread: Number(x.unread || 0),
      pushed: Number(x.pushed || 0),
      resolved: Number(x.resolved || 0),
    }));
  }, [data]);

  const deliveryData = useMemo(() => {
    return (data?.deliveryHealth || []).map((x) => ({
      name: `${x.channel}:${x.status}`,
      value: Number(x.total || 0),
    }));
  }, [data]);

  if (loading) {
    return <div className="p-6">Loading notification analytics...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Notification Control Center</h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor delivery, unread backlog, escalations and response behavior.
          </p>
        </div>

        <button
          onClick={runEscalations}
          className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
        >
          ▶ Run Escalations
        </button>
      </div>

      {actionMsg ? (
        <div className="text-sm text-gray-600 dark:text-neutral-300">
          {actionMsg}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <MetricCard title="Total" value={data?.totals?.total} />
        <MetricCard title="Unread" value={data?.totals?.unread} />
        <MetricCard title="Pushed" value={data?.totals?.pushed} />
        <MetricCard title="Escalated" value={data?.totals?.escalated} />
        <MetricCard title="Resolved" value={data?.totals?.resolved} />
        <MetricCard
          title="Awaiting escalation"
          value={data?.backlog?.awaiting_escalation}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="border rounded-2xl p-4 shadow-sm border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
          <h2 className="text-lg font-semibold mb-4">Notification Funnel</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border rounded-2xl p-4 shadow-sm border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
          <h2 className="text-lg font-semibold mb-4">Delivery Health</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={deliveryData} dataKey="value" nameKey="name" outerRadius={90} label>
                  {deliveryData.map((_, i) => (
                    <Cell key={i} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="border rounded-2xl p-4 shadow-sm border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <h2 className="text-lg font-semibold mb-4">By Type</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={byTypeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="total" />
              <Bar dataKey="unread" />
              <Bar dataKey="pushed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border rounded-2xl p-4 shadow-sm border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        <h2 className="text-lg font-semibold mb-4">Top Users by Unread</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left py-2 pr-4">User UID</th>
                <th className="text-left py-2 pr-4">Total</th>
                <th className="text-left py-2 pr-4">Unread</th>
                <th className="text-left py-2 pr-4">Critical unread</th>
                <th className="text-left py-2 pr-4">Avg first read (sec)</th>
              </tr>
            </thead>
            <tbody>
              {(data?.topUsers || []).map((row) => (
                <tr
                  key={row.user_uid}
                  className="border-b border-gray-100 dark:border-neutral-800"
                >
                  <td className="py-2 pr-4">{row.user_uid}</td>
                  <td className="py-2 pr-4">{row.total}</td>
                  <td className="py-2 pr-4">{row.unread}</td>
                  <td className="py-2 pr-4">{row.critical_unread}</td>
                  <td className="py-2 pr-4">
                    {row.avg_first_read_sec
                      ? Math.round(Number(row.avg_first_read_sec))
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <MetricCard
          title="Push subscriptions"
          value={data?.subscriptions?.total}
          subtitle={`Active: ${data?.subscriptions?.active || 0}`}
        />
      </div>
    </div>
  );
}